/**
 * Statistical utility functions for insight detection
 * Provides foundational statistical calculations for pattern analysis
 */

/**
 * Calculate the mean (average) of an array of numbers
 * @param values - Array of numeric values
 * @returns Mean value, or 0 if array is empty
 */
export function calculateMean(values: number[]): number {
  if (!values || values.length === 0) {
    return 0;
  }
  
  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / values.length;
}

/**
 * Calculate the standard deviation of an array of numbers
 * Uses sample standard deviation (n-1 denominator)
 * @param values - Array of numeric values
 * @returns Standard deviation, or 0 if array has fewer than 2 elements
 */
export function calculateStdDev(values: number[]): number {
  if (!values || values.length < 2) {
    return 0;
  }
  
  const mean = calculateMean(values);
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / (values.length - 1);
  
  return Math.sqrt(variance);
}

/**
 * Calculate the Z-score for a value within a dataset
 * Z-score indicates how many standard deviations a value is from the mean
 * @param value - The value to calculate Z-score for
 * @param mean - Mean of the dataset
 * @param stdDev - Standard deviation of the dataset
 * @returns Z-score, or 0 if stdDev is 0
 */
export function calculateZScore(value: number, mean: number, stdDev: number): number {
  if (stdDev === 0) {
    return 0;
  }
  
  return (value - mean) / stdDev;
}

/**
 * Calculate simple moving average for trend detection
 * @param values - Array of numeric values (should be in chronological order)
 * @param windowSize - Number of periods to include in the moving average
 * @returns Array of moving average values (length = values.length - windowSize + 1)
 */
export function calculateMovingAverage(values: number[], windowSize: number): number[] {
  if (!values || values.length < windowSize || windowSize < 1) {
    return [];
  }
  
  const movingAverages: number[] = [];
  
  for (let i = 0; i <= values.length - windowSize; i++) {
    const window = values.slice(i, i + windowSize);
    const avg = calculateMean(window);
    movingAverages.push(avg);
  }
  
  return movingAverages;
}

/**
 * Calculate the slope of a linear trend line using least squares method
 * Positive slope = upward trend, negative = downward trend
 * @param values - Array of numeric values (chronologically ordered)
 * @returns Slope of the trend line
 */
export function calculateTrendSlope(values: number[]): number {
  if (!values || values.length < 2) {
    return 0;
  }
  
  const n = values.length;
  const indices = Array.from({ length: n }, (_, i) => i);
  
  const meanX = calculateMean(indices);
  const meanY = calculateMean(values);
  
  let numerator = 0;
  let denominator = 0;
  
  for (let i = 0; i < n; i++) {
    numerator += (indices[i] - meanX) * (values[i] - meanY);
    denominator += Math.pow(indices[i] - meanX, 2);
  }
  
  if (denominator === 0) {
    return 0;
  }
  
  return numerator / denominator;
}

/**
 * Determine if a value is statistically significant compared to the mean
 * Uses Z-score with a threshold (default: 1.5 standard deviations)
 * @param value - The value to test
 * @param dataset - The dataset to compare against
 * @param threshold - Z-score threshold for significance (default: 1.5)
 * @returns true if the value is significantly different from the mean
 */
export function isSignificantlyDifferent(
  value: number,
  dataset: number[],
  threshold: number = 1.5
): boolean {
  const mean = calculateMean(dataset);
  const stdDev = calculateStdDev(dataset);
  const zScore = calculateZScore(value, mean, stdDev);
  
  return Math.abs(zScore) >= threshold;
}

/**
 * Calculate percentage change between two values
 * @param current - Current value
 * @param previous - Previous value
 * @returns Percentage change (0-100 scale)
 */
export function calculatePercentageChange(current: number, previous: number): number {
  if (previous === 0) {
    return current > 0 ? 100 : (current < 0 ? -100 : 0);
  }
  
  return ((current - previous) / Math.abs(previous)) * 100;
}
